import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subSeconds,
  subWeeks,
} from 'date-fns';
import moment from 'moment';

export abstract class TimeRangeOption {
  public static ENUM: TimeRangeOption[] = [];

  protected constructor(
    public readonly id: string,
    public readonly defaultRange: number,
    public readonly barCount: number,
    public readonly shownUnit: TimeRangeOption | null,
    public readonly add: (date: number | Date, amount: number) => Date,
    public readonly subtract: (date: number | Date, amount: number) => Date,
  ) {
    TimeRangeOption.ENUM.push(this);
  }

  public abstract getBlockName(date: Date): string;
  public abstract formatTooltipXValue(date: number): string;
  public abstract formatXAxisLabel(date: number): string;
  public abstract roundUp(date: Date): Date;
}

class SecondTimeRangeOption extends TimeRangeOption {
  constructor() {
    super('seconds', 1, 60, null, addSeconds, subSeconds);
  }

  public override getBlockName(date: Date): string {
    return `${date.getMilliseconds()}/${date.getSeconds()}`;
  }

  public override formatTooltipXValue(date: number): string {
    return moment(date).format('SSS');
  }

  public override formatXAxisLabel(date: number): string {
    return moment(date).format('SSS');
  }

  public override roundUp(date: Date): Date {
    let roundedDate = new Date(date);
    if (roundedDate.getMilliseconds() > 0) {
      roundedDate = addSeconds(roundedDate, 1);
      roundedDate.setMilliseconds(0);
    }

    return roundedDate;
  }
}

class MinuteTimeRangeOption extends TimeRangeOption {
  constructor(prev: TimeRangeOption) {
    super('minutes', 1, 60, prev, addMinutes, subMinutes);
  }

  public override getBlockName(date: Date): string {
    return `${date.getSeconds()}/${date.getMinutes()}`;
  }

  public override formatTooltipXValue(date: number): string {
    return moment(date).format('ss');
  }

  public override formatXAxisLabel(date: number): string {
    return moment(date).format('ss');
  }

  public override roundUp(date: Date): Date {
    let roundedDate = new Date(date);
    if (roundedDate.getSeconds() > 0) {
      roundedDate = addMinutes(roundedDate, 1);
      roundedDate.setSeconds(0);
    }

    return roundedDate;
  }
}

class FiveMinuteTimeRangeOption extends TimeRangeOption {
  constructor() {
    // Currently we go with range of 5 minutes while adding and subtracing single minutes
    // maybe in the future it could be more convient to have a range of 1
    // and to add 5 minutes with one adding step of the add method of this class
    super('fiveMinutes', 5, 5, null, addMinutes, subMinutes);
  }

  public override getBlockName(date: Date): string {
    return `${date.getMinutes()}`;
  }

  public override formatTooltipXValue(date: number): string {
    return moment(date).format('HH:mm');
  }

  public override formatXAxisLabel(date: number): string {
    return moment(date).format('HH:mm');
  }

  public override roundUp(date: Date): Date {
    let roundedDate = new Date(date);

    const diffToFive = roundedDate.getMinutes() % 5;
    if (diffToFive > 0) {
      const minutesToAdd = 5 - diffToFive;
      roundedDate = addMinutes(roundedDate, minutesToAdd);
      roundedDate.setSeconds(0);
    }

    return roundedDate;
  }
}

class HourTimeRangeOption extends TimeRangeOption {
  constructor(prev: TimeRangeOption) {
    super('hour', 1, 60, prev, addHours, subHours);
  }

  public override getBlockName(date: Date): string {
    return `${date.getMinutes()}/${date.getHours()}`;
  }

  public override formatTooltipXValue(date: number): string {
    return moment(date).format('mm');
  }

  public override formatXAxisLabel(date: number): string {
    return moment(date).format('mm');
  }

  public override roundUp(date: Date): Date {
    let roundedDate = new Date(date);
    if (roundedDate.getMinutes() > 0) {
      roundedDate = addHours(roundedDate, 1);
      roundedDate.setMinutes(0);
    }

    return roundedDate;
  }
}

class DayTimeRangeOption extends TimeRangeOption {
  constructor(prev: TimeRangeOption) {
    super('day', 1, 24, prev, addDays, subDays);
  }

  public override getBlockName(date: Date): string {
    return `${date.getHours()}/${date.getDate()}`;
  }

  public override formatTooltipXValue(date: number): string {
    return moment(date).format('HH');
  }

  public override formatXAxisLabel(date: number): string {
    return moment(date).format('HH');
  }

  public override roundUp(date: Date): Date {
    let roundedDate = new Date(date);
    if (roundedDate.getHours() > 0) {
      roundedDate = addDays(roundedDate, 1);
      roundedDate.setHours(0);
    }

    return roundedDate;
  }
}

class WeekTimeRangeOption extends TimeRangeOption {
  constructor(prev: TimeRangeOption) {
    super('week', 1, 7, prev, addWeeks, subWeeks);
  }

  public override getBlockName(date: Date): string {
    return `${date.getDate() - 1}/${date.getMonth()}`;
  }

  public override formatTooltipXValue(date: number): string {
    return moment(date).format('ddd');
  }

  public override formatXAxisLabel(date: number): string {
    return moment(date).format('ddd');
  }

  public override roundUp(date: Date): Date {
    let roundedDate = new Date(date);
    if (roundedDate.getHours() > 0) {
      roundedDate = addDays(roundedDate, 1);
      roundedDate.setHours(0);
    }

    return roundedDate;
  }
}

class MonthTimeRangeOption extends TimeRangeOption {
  constructor(prev: TimeRangeOption) {
    super('month', 1, 31, prev, addMonths, subMonths);
  }

  public override getBlockName(date: Date): string {
    return `${date.getDate() - 1}/${date.getMonth()}`;
  }

  public override formatTooltipXValue(date: number): string {
    return moment(date).format('DD.MM');
  }

  public override formatXAxisLabel(date: number): string {
    return moment(date).format('DD');
  }

  public override roundUp(date: Date): Date {
    let roundedDate = new Date(date);
    if (roundedDate.getHours() > 0) {
      roundedDate = addDays(roundedDate, 1);
      roundedDate.setHours(0);
    }

    return roundedDate;
  }
}

export class TimeRangeOptions {
  public static readonly SECONDS = new SecondTimeRangeOption();
  public static readonly MINUTES = new MinuteTimeRangeOption(TimeRangeOptions.SECONDS);
  public static readonly FIVE_MINUTES = new FiveMinuteTimeRangeOption();
  public static readonly HOUR = new HourTimeRangeOption(TimeRangeOptions.MINUTES);
  public static readonly DAY = new DayTimeRangeOption(TimeRangeOptions.HOUR);
  public static readonly WEEK = new WeekTimeRangeOption(TimeRangeOptions.DAY);
  public static readonly MONTH = new MonthTimeRangeOption(TimeRangeOptions.DAY);

  public static getById(id: string): TimeRangeOption | undefined {
    return TimeRangeOption.ENUM.find((value: TimeRangeOption) => {
      return value.id === id;
    });
  }

  public static getValues(): ReadonlyArray<TimeRangeOption> {
    return Object.freeze([...TimeRangeOption.ENUM]);
  }
}
